interface CompanyLike {
  _id: string;
  parentCompanyID?: string | null;
}

interface StationLike {
  _id: string;
  companyID?: string | null;
  country?: string | null;
}

/**
 * The company itself plus its direct subsidiaries, one level deep. Same scope
 * the station batch operation already uses (pages/Stations/BatchOperation).
 */
export const getCompanyScopeIds = (
  company?: CompanyLike | null,
  allCompanies: CompanyLike[] = []
): string[] => {
  if (!company?._id) return [];

  const childIds = (allCompanies ?? [])
    .filter((candidate) => candidate?.parentCompanyID === company._id)
    .map((candidate) => candidate._id)
    .filter(Boolean);

  return Array.from(new Set([company._id, ...childIds]));
};

/**
 * Defensive client-side narrowing: the request already asks the API for these
 * companies, but filtering again keeps the scope correct even if the API
 * ignores the filter and answers with every station.
 */
export const filterStationsByCompanyIds = (
  stations: StationLike[] = [],
  companyIds: string[] = []
): StationLike[] => {
  if (!companyIds.length) return [];

  const scope = new Set(companyIds);
  return (stations ?? []).filter((station) =>
    scope.has(String(station?.companyID))
  );
};

/**
 * The batch update endpoint validates that every station shares a single
 * country, so a company spanning countries needs one request per country.
 * Stations without a country are dropped: they would fail that validation and
 * take the whole request down with them.
 */
export const groupStationIdsByCountry = (
  stations: StationLike[] = []
): Record<string, string[]> =>
  (stations ?? []).reduce<Record<string, string[]>>((groups, station) => {
    if (!station?._id || !station?.country) return groups;

    const country = station.country;
    const current = groups[country] ?? [];
    current.push(String(station._id));
    groups[country] = current;

    return groups;
  }, {});
